import axios, { AxiosInstance } from 'axios'
import type { CreateContactMessageRequest, CreateContactMessageResponse } from '@/types/contact'
import type { LanguageCode } from '@/i18n/types'
import { defaultLanguage, translations } from '@/i18n/translations'

const envBaseUrl = import.meta.env.VITE_API_URL?.trim()
const API_BASE_URL = (envBaseUrl && envBaseUrl.length > 0 ? envBaseUrl : '/api').replace(/\/+$/, '')

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const languageHeader = (error.config?.headers as Record<string, string | undefined> | undefined)?.[
      'X-Client-Language'
    ]
    const requestLanguage =
      languageHeader === 'pt-BR' || languageHeader === 'pt-PT' || languageHeader === 'en'
        ? (languageHeader as LanguageCode)
        : defaultLanguage
    const apiText = translations[requestLanguage]?.api ?? translations[defaultLanguage].api
    const isNetworkOrCorsFailure = !error.response && !!error.request
    const message =
      (isNetworkOrCorsFailure
        ? apiText.networkError
        : null) ??
      error.response?.data?.message ??
      (typeof error.response?.data === 'string' ? error.response.data : null) ??
      error.message ??
      apiText.genericError
    return Promise.reject(new Error(message))
  }
)

/**
 * Envia o formulário de contato para o endpoint POST /api/contact (CreateContactMessageRequest).
 * Backend grava no banco e enfileira envio de e-mail; retorna 201 com id e createdAt.
 */
export const contactService = {
  async send(
    payload: CreateContactMessageRequest,
    language: LanguageCode = defaultLanguage
  ): Promise<CreateContactMessageResponse> {
    const { data } = await api.post<CreateContactMessageResponse>('/contact', payload, {
      headers: {
        'X-Client-Language': language,
      },
    })
    return data
  },
}

export default api
